/**
 * Token Management Utilities
 * Handles JWT token storage, retrieval, and validation
 *
 * Storage Strategy:
 * - "Remember Me" checked: tokens stored in localStorage (persists across sessions)
 * - "Remember Me" unchecked: tokens stored in sessionStorage (cleared on tab close)
 */

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const REMEMBER_ME_KEY = 'remember_me';

/**
 * Get the appropriate storage based on "Remember Me" preference
 */
const getStorage = (): Storage => {
  const rememberMe = localStorage.getItem(REMEMBER_ME_KEY) === 'true';
  return rememberMe ? localStorage : sessionStorage;
};

/**
 * Set the "Remember Me" preference
 */
export const setRememberMe = (rememberMe: boolean): void => {
  if (rememberMe) {
    localStorage.setItem(REMEMBER_ME_KEY, 'true');
  } else {
    localStorage.removeItem(REMEMBER_ME_KEY);
  }
};

/**
 * Get the "Remember Me" preference
 */
export const getRememberMe = (): boolean => {
  return localStorage.getItem(REMEMBER_ME_KEY) === 'true';
};

/**
 * Store access token in appropriate storage
 */
export const setAccessToken = (token: string): void => {
  getStorage().setItem(ACCESS_TOKEN_KEY, token);
};

/**
 * Get access token from storage
 * Checks both localStorage and sessionStorage to handle edge cases
 */
export const getAccessToken = (): string | null => {
  return localStorage.getItem(ACCESS_TOKEN_KEY) || sessionStorage.getItem(ACCESS_TOKEN_KEY);
};

/**
 * Store refresh token in appropriate storage
 */
export const setRefreshToken = (token: string): void => {
  getStorage().setItem(REFRESH_TOKEN_KEY, token);
};

/**
 * Get refresh token from storage
 * Checks both localStorage and sessionStorage to handle edge cases
 */
export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY) || sessionStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Store both access and refresh tokens with optional "Remember Me" handling
 */
export const setTokens = (
  accessToken: string,
  refreshToken: string,
  rememberMe?: boolean
): void => {
  // When rememberMe is explicitly provided, clear tokens from the opposite
  // storage before switching. This prevents stale tokens from lingering when
  // toggling between persistent (localStorage) and session (sessionStorage).
  if (rememberMe !== undefined) {
    const nonTargetStorage = rememberMe ? sessionStorage : localStorage;
    nonTargetStorage.removeItem(ACCESS_TOKEN_KEY);
    nonTargetStorage.removeItem(REFRESH_TOKEN_KEY);

    setRememberMe(rememberMe);
  }

  setAccessToken(accessToken);
  setRefreshToken(refreshToken);
};

/**
 * Remove access token from all storages
 */
export const removeAccessToken = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
};

/**
 * Remove refresh token from all storages
 */
export const removeRefreshToken = (): void => {
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
};

/**
 * Remove all auth-related data from storage
 */
export const clearTokens = (): void => {
  removeAccessToken();
  removeRefreshToken();
  localStorage.removeItem(REMEMBER_ME_KEY);
};

/**
 * Check if user has valid access token
 */
export const hasAccessToken = (): boolean => {
  return !!getAccessToken();
};

/**
 * Parse JWT token payload (without validation)
 * Note: This doesn't verify the signature, just decodes the payload
 */
export const parseJwtPayload = <T = Record<string, unknown>>(token: string): T | null => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload) as T;
  } catch (error) {
    console.error('Failed to parse JWT token:', error);
    return null;
  }
};

/**
 * Check if JWT token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  const payload = parseJwtPayload<{ exp?: number }>(token);
  if (!payload || !payload.exp) return true;

  // exp is in seconds, Date.now() is in milliseconds
  return payload.exp * 1000 < Date.now();
};

/**
 * Get time until token expires (in milliseconds)
 */
export const getTokenExpiresIn = (token: string): number => {
  const payload = parseJwtPayload<{ exp?: number }>(token);
  if (!payload || !payload.exp) return 0;

  const expiresAt = payload.exp * 1000;
  const now = Date.now();

  return Math.max(0, expiresAt - now);
};

/**
 * Check if access token needs refresh (expires in less than 5 minutes)
 */
export const shouldRefreshToken = (): boolean => {
  const token = getAccessToken();
  if (!token) return false;

  const expiresIn = getTokenExpiresIn(token);
  const fiveMinutes = 5 * 60 * 1000;

  return expiresIn < fiveMinutes;
};

/**
 * Check if token is about to expire (within buffer period)
 * Used for proactive refresh
 */
export const isTokenExpiringSoon = (token: string, bufferMs: number = 30000): boolean => {
  const expiresIn = getTokenExpiresIn(token);
  return expiresIn > 0 && expiresIn <= bufferMs;
};

/**
 * Get token expiration time as Date
 */
export const getTokenExpirationDate = (token: string): Date | null => {
  const payload = parseJwtPayload<{ exp?: number }>(token);
  if (!payload || !payload.exp) return null;
  return new Date(payload.exp * 1000);
};

// ============================================================================
// Token Refresh with Deduplication
// ============================================================================

/**
 * Singleton promise for ongoing refresh to prevent multiple simultaneous requests
 */
let refreshPromise: Promise<{ accessToken: string; refreshToken: string }> | null = null;

/**
 * Callback type for token refresh
 */
type TokenRefreshFn = (
  refreshToken: string
) => Promise<{ accessToken: string; refreshToken: string }>;

/**
 * Refresh access token with deduplication
 *
 * Prevents multiple simultaneous refresh requests by sharing a single promise.
 * If a refresh is already in progress, subsequent calls will receive the same promise.
 *
 * @param refreshFn - The function to call the refresh API
 * @returns Promise resolving to new tokens
 * @throws Error if no refresh token or refresh fails
 *
 * @example
 * ```ts
 * const newTokens = await refreshAccessToken(async (token) => {
 *   const response = await authApi.refreshToken(token);
 *   return response;
 * });
 * ```
 */
export const refreshAccessToken = async (
  refreshFn: TokenRefreshFn
): Promise<{ accessToken: string; refreshToken: string }> => {
  // Return existing promise if refresh is in progress
  if (refreshPromise) {
    return refreshPromise;
  }

  const currentRefreshToken = getRefreshToken();
  if (!currentRefreshToken) {
    throw new Error('No refresh token available');
  }

  // Create new refresh promise
  refreshPromise = refreshFn(currentRefreshToken)
    .then((tokens) => {
      // Store new tokens (preserving remember me preference)
      setTokens(tokens.accessToken, tokens.refreshToken);
      return tokens;
    })
    .finally(() => {
      // Clear the promise so next refresh can proceed
      refreshPromise = null;
    });

  return refreshPromise;
};

/**
 * Check if a token refresh is currently in progress
 */
export const isRefreshInProgress = (): boolean => {
  return refreshPromise !== null;
};
